export const isUnlimitedSeats = (value?: number | null): boolean => Number(value) === -1;

export const formatSeatsLabel = (value?: number | null): string =>
  isUnlimitedSeats(value) ? "Unlimited" : String(Number(value) || 0);

export const hasSeatAccess = (value?: number | null): boolean =>
  isUnlimitedSeats(value) || Number(value) > 0;

export interface PurchasePoolNumbers {
  quotaPurchased: number;
  quotaUsed: number;
  quotaAvailable: number;
}

export interface FreePoolNumbers {
  quota: number;
  quotaUsed: number;
  quotaAvailable: number;
}

export interface CombineSeatMetricsParams {
  purchase: PurchasePoolNumbers | null | undefined;
  hasPurchaseAccess: boolean;
  free: FreePoolNumbers | null | undefined;
  /**
   * When true, the scoped assignment is free access — show that pool only.
   * Otherwise show the org pool from `purchaseInfo` (company `course_assignments` row).
   * We no longer add paid + free; both come from the same table and would double-count.
   */
  assignmentIsFreeAccess?: boolean;
}

/**
 * One display row for org seats: either the free scoped grant, or the company assignment pool
 * (`purchaseInfo`), never the sum of both.
 */
export function combineSeatMetrics(params: CombineSeatMetricsParams): { total: number; used: number; remaining: number } | null {
  const { purchase, hasPurchaseAccess, free, assignmentIsFreeAccess } = params;
  const hasP = Boolean(hasPurchaseAccess) && purchase != null;
  const hasF = free != null;

  if (!hasP && !hasF) {
    return null;
  }

  if (assignmentIsFreeAccess && hasF) {
    const fUnlim = isUnlimitedSeats(free!.quota) || isUnlimitedSeats(free!.quotaAvailable);
    if (fUnlim) {
      return {
        total: -1,
        used: Number(free!.quotaUsed || 0),
        remaining: -1,
      };
    }
    return {
      total: Number(free!.quota || 0),
      used: Number(free!.quotaUsed || 0),
      remaining: Number(free!.quotaAvailable || 0),
    };
  }

  if (hasP) {
    const pUnlim =
      isUnlimitedSeats(purchase!.quotaPurchased) || isUnlimitedSeats(purchase!.quotaAvailable);
    if (pUnlim) {
      return {
        total: -1,
        used: Number(purchase!.quotaUsed || 0),
        remaining: -1,
      };
    }
    return {
      total: Number(purchase!.quotaPurchased || 0),
      used: Number(purchase!.quotaUsed || 0),
      remaining: Number(purchase!.quotaAvailable || 0),
    };
  }

  if (hasF) {
    const fUnlim = isUnlimitedSeats(free!.quota) || isUnlimitedSeats(free!.quotaAvailable);
    if (fUnlim) {
      return {
        total: -1,
        used: Number(free!.quotaUsed || 0),
        remaining: -1,
      };
    }
    return {
      total: Number(free!.quota || 0),
      used: Number(free!.quotaUsed || 0),
      remaining: Number(free!.quotaAvailable || 0),
    };
  }

  return null;
}
